import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { i18n } from 'shared/i18n';
import { api } from 'shared/api/client';
import type { paths } from 'shared/api/generated/api';
import { templateKeys } from './keys';

type TemplateBody = NonNullable<paths['/templates/']['post']['requestBody']>['content']['application/json'];

interface TemplateStructure {
  templateSuperId?: string;
  [key: string]: unknown;
}

export interface Template {
  superId?: string;
  revisionId?: string;
  documentSuperId?: number | null;
  organization?: number | null;
  structure?: unknown;
  style?: string;
  type?: string;
  className?: string | null;
  name?: string;
  thumbnail?: unknown;
}

type TemplateType = 'document' | 'template' | 'style-preview';
const boolParam = (v: boolean | undefined) => (v === undefined ? undefined : v ? 'true' : 'false');

export function useTemplates({
  organization,
  documentSuperId,
  type,
  selected = undefined,
}: {
  organization?: unknown;
  documentSuperId?: number | string;
  type?: string;
  selected?: boolean;
}) {
  return useQuery({
    queryKey: templateKeys.list(organization, documentSuperId, type, selected),

    queryFn: async () => {
      const { data } = await api.GET('/templates/', {
        params: {
          query: {
            organization: organization != null ? Number(organization) : undefined,
            documentSuperId: documentSuperId != null ? Number(documentSuperId) : undefined,
            type: type as TemplateType,
            selected: boolParam(selected),
          },
        },
      });
      return data;
    },
  });
}

export function useDocumentTemplates(documentSuperId: number | string) {
  return useQuery({
    queryKey: templateKeys.forDocument(documentSuperId),

    queryFn: async () => {
      const { data } = await api.GET('/templates/document/{documentSuperId}', {
        params: { path: { documentSuperId: Number(documentSuperId) } },
      });
      return data;
    },

    enabled: !!documentSuperId,
  });
}

export function useTemplate(revisionId: string) {
  return useQuery({
    queryKey: templateKeys.detail(revisionId),

    queryFn: async () => {
      const { data } = await api.GET('/templates/{revisionId}', {
        params: { path: { revisionId } },
      });
      return data;
    },

    enabled: !!revisionId,
  });
}

export function useSaveTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (template: Template) => {
      await api.POST('/templates/', { body: template as TemplateBody });
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...templateKeys.listAll],
      });
    },
  });
}

export function useCopyTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (template: Template) => {
      const newTemplate = {
        ...template,
        superId: uuidv4(),
        revisionId: uuidv4(),
        name: `${i18n('templates:copyof')} ${template.name}`,
        className: '',
      };
      const { data } = await api.POST('/templates/', { body: newTemplate as TemplateBody });
      return data;
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...templateKeys.listAll],
      });
    },
  });
}

export function usePromoteTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (template: Template) => {
      const newTemplate = {
        ...template,
        revisionId: uuidv4(),
        organization: null,
      };
      const { data } = await api.POST('/templates/', { body: newTemplate as TemplateBody });
      return data;
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...templateKeys.listAll],
      });
    },
  });
}

export function useDemoteTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ template, organization }: { template: Template; organization: unknown }) => {
      const newTemplate = {
        ...template,
        revisionId: uuidv4(),
        organization,
      };
      const { data } = await api.POST('/templates/', { body: newTemplate as TemplateBody });
      return data;
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...templateKeys.listAll],
      });
    },
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (template: { superId: string; revisionId?: string }) => {
      await api.DELETE('/templates/{superId}', { params: { path: { superId: template.superId } } });
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...templateKeys.listAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...templateKeys.detailAll],
      });
    },
  });
}

export function useSelectedTemplates(documentSuperId: number | string) {
  return useQuery({
    queryKey: templateKeys.selected(documentSuperId),

    queryFn: async () => {
      const { data } = await api.GET('/templates/selected/{templateSuperId}', {
        params: { path: { templateSuperId: Number(documentSuperId) } },
      });
      return data;
    },
  });
}

export function useUpdateSelectedTemplates(documentSuperId: number | string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateIds: string[]) => {
      await api.PUT('/templates/selected/{templateSuperId}', {
        params: { path: { templateSuperId: Number(documentSuperId) } },
        body: templateIds,
      });
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...templateKeys.selectedAll],
      });
    },
  });
}

export function useCompiledTemplate(superId: string, organization: unknown, documentSuperId?: number | string) {
  return useQuery({
    queryKey: templateKeys.compiled(superId, organization, documentSuperId),

    queryFn: async () => {
      const { data } = await api.GET('/templates/', {
        params: {
          query: {
            superId,
            organization: Number(organization),
            type: 'template',
            compiled: 'true',
            ...(documentSuperId ? { documentSuperId: Number(documentSuperId) } : {}),
          },
        },
      });
      const template = (data as Template[] | undefined)?.[0];
      if (template) {
        (template.structure as TemplateStructure).templateSuperId = template.superId;
      }
      return template;
    },

    enabled: !!superId && !!organization,
    placeholderData: keepPreviousData,
  });
}

export function useSaveCompiledTemplate(documentSuperId?: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (template: Template) => {
      const { data } = await api.POST('/templates/', {
        body: template as TemplateBody,
        params: { query: { compiled: 'true', ...(documentSuperId ? { documentSuperId: Number(documentSuperId) } : {}) } },
      });
      return data;
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...templateKeys.listAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...templateKeys.detailAll],
      });
    },
  });
}
